import type { FormSettings } from "@/api/assignments";
import type { Lesson } from "@/api/lessons";
import type { CategoryScore, TopicScore } from "@/constants/assessmentsReport.constants";
import {
  COMPLETED_STATUS_CODE,
  INPROGRESS_STATUS_CODE,
  NOT_STARTED_STATUS_CODE,
  OVERDUE_STATUS_CODE,
  resettableStatusCodes,
} from "@/constants/assessmentsReport.constants";
import { getProficiencyFromScore } from "@/helpers/assessmentsReport";
import { normalizeNotificationsFrequency } from "@/helpers/assignmentHelpers";
import {
  getRailsClient,
  getRailsFormClient,
  LessonsByTopicResponse,
} from "sj-ui-toolkit/src/client";
import { KA_STATUS_KEYS } from "sj-ui-toolkit/src/constants/assessmentStatus";
import {
  API_ASSIGNMENT_POLICIES_PATH,
  apiAssignmentPath,
} from "sj-ui-toolkit/src/constants/routes";
// ─── Types ────────────────────────────────────────────────────────────────────

export interface LessonNode {
  id?: string | number;
  key?: string;
  label?: string;
  lesson_key?: string;
  lessonKey?: string;
  children?: LessonNode[];
  customClassName?: string;
  averageScore?: number;
  duration?: number | string;
  required?: boolean;
  [key: string]: unknown;
}

export interface LearnerScore {
  directoryUuid?: string;
  email?: string;
  givenName?: string;
  familyName?: string;
  averageScore?: number | null;
  proficiency?: string | null;
  status?: string;
  resettable?: boolean;
  isOverdue?: boolean;
  scores?: Array<{
    notApplicable?: boolean;
    topic?: { names: string[] };
    details?: { earnedPoints: number; possiblePoints: number };
  }>;
  timeSpent?: string;
  completedAt?: string | null;
  statusCode?: string;
}

export interface TopicScores {
  categoryScoresAverage: CategoryScore[];
  topicScoresAverage: Record<string, TopicScore[]>;
}

export interface ScoreData {
  topicScores: TopicScores;
  learnerScores: LearnerScore[];
}

export interface CompleteAssignmentResult {
  role: { id: string; [key: string]: unknown };
  policy: { id: string; [key: string]: unknown };
}

// ─── URL constants ────────────────────────────────────────────────────────────

const API_V1 = "/api/internal_api/v1/admin";

// ─── Status constants ─────────────────────────────────────────────────────────

const KA_REPORTING_STATUSES = {
  notStarted: "not_started",
  submitted: "submitted",
  started: "started",
};

// ─── Score helpers ────────────────────────────────────────────────────────────

const calculateLearnerScores = (
  scores: Array<{ details?: { earnedPoints: number; possiblePoints: number } }>
): number | null => {
  if (!scores.length) return null;
  const totalPoints = scores.reduce((acc, s) => acc + (s.details?.earnedPoints ?? 0), 0);
  const totalPossible = scores.reduce((acc, s) => acc + (s.details?.possiblePoints ?? 0), 0);
  if (totalPossible === 0) return null;
  return Math.round((totalPoints / totalPossible) * 100);
};

export const addColumnsToAssessmentReport = (reports: LearnerScore[]): LearnerScore[] => {
  return reports.map(report => {
    let status: string;
    let resettable = false;

    if (report.statusCode) {
      const statusCodeMap: Record<string, string> = {
        [COMPLETED_STATUS_CODE]: KA_STATUS_KEYS.complete,
        [INPROGRESS_STATUS_CODE]: KA_STATUS_KEYS.inProgress,
        [NOT_STARTED_STATUS_CODE]: KA_STATUS_KEYS.notStarted,
        [OVERDUE_STATUS_CODE]: KA_STATUS_KEYS.overdue,
      };
      status = statusCodeMap[report.statusCode] ?? KA_STATUS_KEYS.notStarted;
      resettable = resettableStatusCodes.includes(report.statusCode);
    } else if (report.status === KA_REPORTING_STATUSES.submitted) {
      status = KA_STATUS_KEYS.complete;
      resettable = true;
    } else if (report.isOverdue) {
      status = KA_STATUS_KEYS.overdue;
      resettable = false;
    } else if (report.status === KA_REPORTING_STATUSES.started && report.timeSpent !== "0s") {
      status = KA_STATUS_KEYS.inProgress;
      resettable = true;
    } else {
      status = KA_STATUS_KEYS.notStarted;
      resettable = false;
    }

    const scores = report.scores ?? [];
    const completedScores = scores.filter(s => !s.notApplicable);
    const averageScore = calculateLearnerScores(completedScores);
    const proficiency = getProficiencyFromScore(averageScore);

    return { ...report, status, averageScore, proficiency, resettable };
  });
};

export const filterTopicsIntoPerformanceBuckets = (
  topicsScoresAverage: TopicScore[]
): { low: TopicScore[]; medium: TopicScore[]; high: TopicScore[] } => {
  const buckets = {
    low: [] as TopicScore[],
    medium: [] as TopicScore[],
    high: [] as TopicScore[],
  };
  topicsScoresAverage.forEach(topic => {
    if (topic.averageScore < 50) buckets.low.push(topic);
    else if (topic.averageScore < 80) buckets.medium.push(topic);
    else buckets.high.push(topic);
  });
  return buckets;
};

// ─── Tree helpers ─────────────────────────────────────────────────────────────

export const buildLessonTreeFromTaxonomy = (taxonomy: LessonsByTopicResponse): LessonNode[] => {
  return (taxonomy.categories ?? [])
    .map(category => ({
      id: category.categoryId,
      key: category.categoryId,
      label: category.title,
      children: (category.topics ?? [])
        .filter(topic => (topic.lessons ?? []).length > 0)
        .map(topic => ({
          id: topic.topicId,
          key: topic.topicId,
          label: topic.title,
          children: (topic.lessons ?? []).map(lesson => ({
            id: lesson.lessonKey,
            key: `${topic.topicId}::${lesson.lessonKey}`,
            label: lesson.name,
            lesson_key: lesson.lessonKey,
            description: lesson.description,
            duration: lesson.duration ?? undefined,
            lessonType: lesson.lessonType,
          })),
        })),
    }))
    .filter(category => (category.children?.length ?? 0) > 0);
};

export const getNodeLevel = (node: LessonNode): "proficiency" | "category" | "topic" | "lesson" => {
  if (!node.children || node.children.length === 0) return "lesson";
  const hasGrandchildren = node.children.some(c => c.children && c.children.length > 0);
  const hasGreatGrandchildren = node.children.some(c =>
    c.children?.some(gc => gc.children && gc.children.length > 0)
  );
  if (hasGreatGrandchildren) return "proficiency";
  if (hasGrandchildren) return "category";
  return "topic";
};

export const getUniqueLessonKeys = (tree: LessonNode[]): Set<string> => {
  const keys = new Set<string>();
  const dfs = (node: LessonNode) => {
    const key = node.lesson_key ?? (node.lessonKey as string | undefined);
    if (key) keys.add(key);
    node.children?.forEach(dfs);
  };
  tree.forEach(dfs);
  return keys;
};

export const getLessonKeyFromNodeKey = (nodeKey: string): string =>
  nodeKey.split("::").pop() ?? nodeKey;

export const calculatePathDuration = (
  lessonMap: Record<string, Lesson>,
  selectedLessons: string[]
): string => {
  if (!selectedLessons.length || !Object.keys(lessonMap).length) return "0 minutes";

  let totalMinutes = 0;
  selectedLessons.forEach(key => {
    const lesson = lessonMap[key];
    if (lesson?.duration) {
      const d = parseInt(String(lesson.duration), 10);
      if (!isNaN(d)) totalMinutes += d;
    }
  });

  if (totalMinutes === 0) return "0 minutes";
  if (totalMinutes < 60) return `${totalMinutes} minute${totalMinutes === 1 ? "" : "s"}`;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours} hour${hours === 1 ? "" : "s"}${minutes > 0 ? ` ${minutes} minute${minutes === 1 ? "" : "s"}` : ""}`;
};

export const normalizeSlug = (str: string): string =>
  str
    .trim()
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[\s_]+/g, "-")
    .replace(/[^a-z0-9-]/g, "");

export const addPerformanceCategorization = (
  treeData: LessonNode[],
  performanceData: Record<string, TopicScore[]>
): LessonNode[] => {
  const LOW_THRESHOLD = 60;
  const HIGH_THRESHOLD = 80;

  const buckets: {
    low: { prefix: string; list: LessonNode[] };
    medium: { prefix: string; list: LessonNode[] };
    high: { prefix: string; list: LessonNode[] };
  } = {
    low: { prefix: "low", list: [] },
    medium: { prefix: "medium", list: [] },
    high: { prefix: "high", list: [] },
  };

  treeData.forEach(category => {
    const perfArr = performanceData[category.label ?? ""] ?? [];
    if (!perfArr.length) return;

    // Prefer matching on the exam's canonical topic path.
    //
    // Fall back to a normalized display-label match
    // for older/prepopulated score data that may not carry a `path`.
    const subById: Record<string, LessonNode> = {};
    const subBySlug: Record<string, LessonNode> = {};
    (category.children ?? []).forEach(sub => {
      if (sub.id) subById[String(sub.id)] = sub;
      if (sub.label) subBySlug[normalizeSlug(sub.label)] = sub;
    });

    const perBucketSubs: { low: LessonNode[]; medium: LessonNode[]; high: LessonNode[] } = {
      low: [],
      medium: [],
      high: [],
    };

    perfArr.forEach(({ topic, path, averageScore }) => {
      const node = (path && subById[path]) || subBySlug[normalizeSlug(topic)];
      if (!node) return;

      const bucketKey: "low" | "medium" | "high" =
        averageScore < LOW_THRESHOLD ? "low" : averageScore <= HIGH_THRESHOLD ? "medium" : "high";
      const cloned: LessonNode = { ...node, averageScore };
      if (node.children) cloned.children = node.children.map(l => ({ ...l }));
      perBucketSubs[bucketKey].push(cloned);
    });

    (Object.keys(perBucketSubs) as Array<keyof typeof perBucketSubs>).forEach(key => {
      perBucketSubs[key].sort((a, b) => (a.averageScore ?? 0) - (b.averageScore ?? 0));
    });

    (Object.keys(buckets) as Array<keyof typeof buckets>).forEach(k => {
      const { prefix, list } = buckets[k];
      const subs = perBucketSubs[k];
      if (subs.length) {
        list.push({
          id: category.id,
          key: `${prefix}-${category.key ?? ""}`,
          label: category.label,
          children: subs,
        });
      }
    });
  });

  return (
    [
      {
        id: "growth-opportunities",
        key: "growth-opportunities",
        label: "Growth Opportunities",
        children: buckets.low.list,
      },
      {
        id: "proficiencies",
        key: "proficiencies",
        label: "Proficiencies",
        children: buckets.medium.list,
      },
      { id: "strengths", key: "strengths", label: "Strengths", children: buckets.high.list },
    ] as LessonNode[]
  ).filter(b => (b.children?.length ?? 0) > 0);
};

export const addTooltipClassesToTreeData = (
  nodes: LessonNode[],
  customClassMapper?: (node: LessonNode, level: string, base: string[]) => string[] | string | null
): LessonNode[] => {
  const processNode = (node: LessonNode): LessonNode => {
    const level = getNodeLevel(node);
    const classes: string[] = [];
    switch (level) {
      case "proficiency":
        classes.push("tree-level-1");
        break;
      case "category":
        classes.push("tree-level-2");
        break;
      case "topic":
        classes.push("tree-level-3");
        break;
      case "lesson":
        classes.push("tree-level-4");
        break;
    }
    if (customClassMapper) {
      const extra = customClassMapper(node, level, [...classes]);
      if (Array.isArray(extra)) classes.push(...extra);
      else if (typeof extra === "string") classes.push(extra);
    }
    const processedNode: LessonNode = { ...node, customClassName: classes.join(" ") };
    if (node.children) processedNode.children = node.children.map(processNode);
    return processedNode;
  };
  return nodes.map(processNode);
};

// ─── Role / Assignment creation ───────────────────────────────────────────────

interface RolePayload {
  name?: string;
  description?: string;
  activated?: boolean;
  archived?: boolean;
  levelId?: number;
  lessonGroupIds?: string[];
  requiredLessonIds?: string[];
  levelType?: string;
  locale?: string;
  reassign?: boolean;
  includeCustomCertDescription?: boolean;
  restricted?: boolean;
}

interface CreatedRole {
  id: string;
  [key: string]: unknown;
}

const createRolePayload = (settings: FormSettings, lessonKeys: string[]): RolePayload => ({
  levelId: 1,
  archived: false,
  activated: settings.activated !== undefined ? settings.activated : true,
  name: settings.name,
  description: (settings as FormSettings & { description?: string }).description,
  lessonGroupIds: lessonKeys,
  requiredLessonIds: lessonKeys,
  levelType: "course",
  locale: "en",
  reassign: true,
  includeCustomCertDescription: false,
  restricted: true,
});

const createRole = async (payload: RolePayload): Promise<{ role: CreatedRole }> => {
  const formData = new FormData();
  formData.append("activated", String(payload.activated ?? true));
  formData.append("archived", String(payload.archived ?? false));
  formData.append("name", payload.name ?? "");
  formData.append("description", payload.description ?? "");
  formData.append("lesson_group_ids", (payload.lessonGroupIds ?? []).join(","));
  formData.append("required_lesson_ids", (payload.requiredLessonIds ?? []).join(","));
  formData.append("level_id", String(payload.levelId ?? 1));
  formData.append("level_type", payload.levelType ?? "course");
  formData.append("locale", payload.locale ?? "en");
  formData.append("reassign", String(payload.reassign ?? true));
  formData.append(
    "include_custom_cert_description",
    String(payload.includeCustomCertDescription ?? false)
  );
  formData.append("restricted", String(payload.restricted ?? false));

  const { data } = await getRailsFormClient().post<{
    role?: CreatedRole;
  }>(`${API_V1}/roles`, formData);

  const role = data.role;
  if (!role) throw new Error("No role data returned from API");
  return { role };
};

const createPolicyPayload = (settings: FormSettings, roleId: string) => {
  const isDynamic = settings.dates?.dynamic ?? false;
  let dueDateAt: number | null = null;
  if (!isDynamic && settings.endDate) {
    dueDateAt = Math.floor(new Date(settings.endDate).getTime() / 1000);
  }
  const daysValue = settings.dates?.dueAfter?.days ?? 0;

  return {
    name: settings.name ?? "",
    description: (settings as FormSettings & { description?: string }).description ?? "",
    activated: settings.activated !== undefined ? settings.activated : true,
    dueDateAt,
    // Rails expects lowercase. Normalise and fall back to "none".
    notificationsFrequency: normalizeNotificationsFrequency(settings.sendReminders),
    roleId,
    duration: isDynamic ? Number(daysValue) : 0,
    enrollmentDelay: 0,
    dateMode: isDynamic ? "dynamic" : "dueDate",
  };
};

// ─── Main orchestrator ────────────────────────────────────────────────────────

export interface RecommendedFormValues {
  orderedLessonKeys: string[];
  target: {
    users: Array<{ email?: string; directoryUuid?: string }>;
    availableUsers?: Array<{ email?: string; directoryUuid?: string }>;
  };
  settings: FormSettings & { description?: string };
}

export const createCompleteAssignment = async (
  formValues: RecommendedFormValues,
  onProgress: (step: { value: number; label: string }) => void
): Promise<CompleteAssignmentResult> => {
  onProgress({ value: 25, label: "Validating data..." });

  const lessonKeys = formValues.orderedLessonKeys;

  if (lessonKeys.length === 0) throw new Error("Please select at least one lesson");
  if (!formValues.target.users.length) throw new Error("Please add at least one user");

  onProgress({ value: 50, label: "Creating Path..." });

  const rolePayload = createRolePayload(formValues.settings, lessonKeys);
  const roleData = await createRole(rolePayload).catch(err => {
    throw new Error(`Path creation failed: ${err instanceof Error ? err.message : String(err)}`);
  });

  onProgress({ value: 75, label: "Creating Assignment..." });

  const policyPayload = createPolicyPayload(formValues.settings, roleData.role.id);
  const { data: policyData } = await getRailsClient()
    .post<{ assignmentPolicy?: { id: string; [key: string]: unknown } }>(
      API_ASSIGNMENT_POLICIES_PATH,
      policyPayload
    )
    .catch(err => {
      throw new Error(
        `Assignment creation failed: ${err instanceof Error ? err.message : String(err)}`
      );
    });

  const policyId = policyData.assignmentPolicy?.id;
  if (!policyId) throw new Error("No assignment policy ID returned");

  onProgress({ value: 90, label: "Assigning to users..." });

  const userEmails = formValues.target.users
    .map(u => u.email)
    .filter(Boolean)
    .join(",");

  await getRailsClient()
    .post(apiAssignmentPath(policyId), { user_emails: userEmails, query: "" })
    .catch(err => {
      throw new Error(
        `Assigning users failed: ${err instanceof Error ? err.message : String(err)}`
      );
    });

  onProgress({ value: 100, label: "Complete!" });

  return {
    role: roleData.role as CompleteAssignmentResult["role"],
    policy: policyData.assignmentPolicy as CompleteAssignmentResult["policy"],
  };
};
